import classNames from 'classnames';
import { Location } from 'history';
import { get } from 'lodash-es';
import React, { Fragment } from 'react';

import PlusIcon from '@sbu/assets.svg/src/add.svg';
import MinusIcon from '@sbu/assets.svg/src/remove.svg';
import { IconButton, useTheme } from '@sumologic/yama-ds-components';

import {
  getRecordSchemaVersion,
  RecordType,
  RecordValue,
} from 'shared/components/internal/Records/Record';
import { Date, Time } from 'shared/components/internal/Timestamp';

import { type EntityTypeFieldsAndIndicator } from '../../useEntityTypeRecordFields';
import { RecordAttribute } from './RecordAttribute';
import css from './RecordHeader.module.scss';
import { useRecordHeaderColumns } from './useRecordHeaderColumns';

const colClassNames = [
  css.firstCol,
  css.secondCol,
  css.thirdCol,
  css.fourthCol,
  css.fifthCol,
  css.sixthCol,
];

export interface RecordHeaderProps {
  expanded: boolean;
  location: Location;
  record: RecordType;
  showExpander: boolean;
  onExpandCollapse: (expanded: boolean) => void;
  entityTypeFieldsAndIndicator?: EntityTypeFieldsAndIndicator;
}

export const RecordHeader = ({
  expanded,
  location,
  onExpandCollapse,
  record,
  showExpander,
  entityTypeFieldsAndIndicator,
}: RecordHeaderProps) => {
  const columns = useRecordHeaderColumns(location, record);
  const { palette } = useTheme();

  const schemaVersion = getRecordSchemaVersion(record);
  const { timestamp } = record;

  // The legacy schema expects timestamps to be in UNIX format as seconds. The
  // Schema v3 expects timestamps to be in UNIX format as milliseconds.
  const isTimestampUnix = schemaVersion === 'legacy';

  return (
    <div
      className={classNames(css.details, {
        [css.noExpand]: !showExpander,
        [css.twoCol]: columns.length === 2,
        [css.threeCol]: columns.length === 3,
      })}
    >
      {showExpander && (
        <div className={css.expanderCol}>
          <IconButton
            size='large'
            onClick={() => onExpandCollapse(!expanded)}
            sx={{
              border: 1,
              borderColor: palette.tokens.YamaBorderVariantLowcontrastColor,
              backgroundColor: 'transparent',
              color: palette.tokens.YamaTypographyColorIcon,
              height: 32,
              margin: 0,
              padding: 0,
              width: 32,
              '&, &:hover, &:focus': {
                stroke: 'none',
              },
            }}
          >
            {expanded ? <MinusIcon /> : <PlusIcon />}
          </IconButton>
        </div>
      )}
      <RecordAttribute
        className={css.timestampCol}
        label='Timestamp'
        record={record}
        subLabel={
          <Time className={css.time} unix={isTimestampUnix}>
            {timestamp}
          </Time>
        }
      >
        <Date className={css.date} unix={isTimestampUnix}>
          {timestamp}
        </Date>
      </RecordAttribute>
      {record.hasOwnProperty('objectType') && (
        <RecordAttribute
          className={css.objectTypeCol}
          label='Object Type'
          record={record}
        >
          {record.objectType}
        </RecordAttribute>
      )}
      {columns.map(
        (
          {
            primaryLabel,
            primaryName,
            primaryRenderer,
            secondaryLabel,
            secondaryName,
            secondaryRenderer,
          },
          index
        ) => {
          const primaryValue =
            (primaryName && get(record, primaryName)) || 'None';
          const primary = primaryRenderer
            ? primaryRenderer(primaryValue, record)
            : primaryValue;

          // The secondary value may not be defined.
          const secondaryValue = secondaryName && get(record, secondaryName);
          const secondary = secondaryRenderer
            ? secondaryRenderer(secondaryValue, record)
            : secondaryValue;

          const fieldHasIndicator =
            entityTypeFieldsAndIndicator?.entityTypeFields?.find(
              (field) => field.field === primaryName
            );

          return (
            <RecordAttribute
              className={colClassNames[index]}
              key={`record-${index}`}
              label={primaryLabel}
              record={record}
              subLabel={
                secondaryValue || secondaryRenderer ? (
                  <Fragment>
                    {!!secondaryLabel && <span>{secondaryLabel}: </span>}
                    {secondaryName ? (
                      <RecordValue name={secondaryName} record={record}>
                        {secondary}
                      </RecordValue>
                    ) : (
                      secondary
                    )}
                  </Fragment>
                ) : undefined
              }
            >
              {primaryName ? (
                <RecordValue
                  entityIndicatorData={
                    fieldHasIndicator
                      ? entityTypeFieldsAndIndicator?.entityIndicatorData
                      : undefined
                  }
                  name={primaryName}
                  record={record}
                >
                  {primary}
                </RecordValue>
              ) : (
                primary
              )}
            </RecordAttribute>
          );
        }
      )}
    </div>
  );
};
